/**
 * Department Details Component
 * 
 * Displays detailed information about a specific department including
 * basic details, assigned staff, and rendering providers.
 * Supports URL-based navigation with department ID persistence.
 * 
 * @component
 * @example
 * return <DepartmentDetails />
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { Box, Typography, Grid, Card, CardContent, Chip, IconButton, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import CustomContainer from '../common/CustomContainer';
import ThemedTooltip from '../common/ThemedTooltip';
import ThemedChip from '../common/ThemedChip';
import StatusToggleButton from '../common/StatusToggleButton';
import { useTheme } from '../../ThemeContext';
import { useDetectMyRole } from '../../custom-hooks';
import { useStatusToggle } from '../../custom-hooks';
import DepartmentService from '../../services/department-service';
import { getFacilities } from '../../utils/apiClient';
import { formatPhoneNumberForList } from '../../utils/helpers/format-phone-number';
import { MODULE_PERMISSIONS } from '../../constants/filter-constants';

const DepartmentDetails = () => {
  const { id: departmentId } = useParams();
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const { role } = useDetectMyRole();

  // State
  const [department, setDepartment] = useState(null);
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Permission checks
  const canEdit = MODULE_PERMISSIONS.department.edit.includes(role);

  // Status toggle functionality
  const {
    statusDialogOpen,
    itemToToggle,
    statusLoading,
    toggleAction,
    handleStatusToggle,
    handleStatusConfirm,
    handleStatusCancel
  } = useStatusToggle(DepartmentService, 'department', () => {
    // Refresh department data after status toggle
    fetchDepartmentDetails();
  });

  /**
   * Fetches department details with related data
   */
  const fetchDepartmentDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch department with staff and providers
      const response = await DepartmentService.getById(departmentId, {
        includeStaff: true,
        includeRenderingProviders: true
      });

      if (response.success) {
        setDepartment(response.data);
        
        // Fetch facility details if we have a facilityId
        if (response.data.facilityId) {
          await fetchFacilityDetails(response.data.facilityId);
        }
      } else {
        setError(response.message || 'Failed to load department details');
        message.error(response.message || 'Failed to load department details');
      }
    } catch (error) {
      console.error('Error fetching department details:', error);
      setError('Failed to load department details. Please try again.');
      message.error('Failed to load department details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches facility details for the department
   */
  const fetchFacilityDetails = async (facilityId) => {
    try {
      const facilities = await getFacilities({ facility_id: facilityId });
      if (facilities && facilities.length > 0) {
        setFacility(facilities[0]);
      }
    } catch (error) {
      console.error('Error fetching facility details:', error);
      // Don't show error message for facility fetch failure
    }
  };

  /**
   * Handles navigation back to departments list
   */
  const handleBack = () => {
    navigate('/departments');
  };

  /**
   * Handles editing department
   */
  const handleEdit = () => {
    navigate('/departments', { 
      state: { 
        editDepartment: department,
        returnToDepartmentId: departmentId 
      } 
    });
  };

  // Fetch department details on component mount
  useEffect(() => {
    if (departmentId) {
      fetchDepartmentDetails();
    } else {
      setError('Department ID not found');
      setLoading(false);
    }
  }, [departmentId]);

  // Loading state
  if (loading) {
    return (
      <CustomContainer containerWidth="90%">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="h6" sx={{ color: currentTheme.text.primary }}>
            Loading department details...
          </Typography>
        </Box>
      </CustomContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <CustomContainer containerWidth="90%">
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton 
              onClick={handleBack}
              sx={{ mr: 2, color: currentTheme.text.primary }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ color: currentTheme.text.primary }}>
              Department Details
            </Typography>
          </Box>
          
          <Card sx={{ backgroundColor: currentTheme.callhistory.secondaryBg }}>
            <CardContent>
              <Typography variant="h6" color="error" gutterBottom>
                Error Loading Department
              </Typography>
              <Typography variant="body1" sx={{ color: currentTheme.text.secondary }}>
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </CustomContainer>
    );
  }

  // No department data
  if (!department) {
    return (
      <CustomContainer containerWidth="90%">
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton 
              onClick={handleBack}
              sx={{ mr: 2, color: currentTheme.text.primary }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ color: currentTheme.text.primary }}>
              Department Details
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ color: currentTheme.text.secondary }}>
            Department not found.
          </Typography>
        </Box>
      </CustomContainer>
    );
  }

  return (
    <CustomContainer containerWidth="90%">
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={handleBack}
              sx={{ mr: 2, color: currentTheme.text.primary }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ color: currentTheme.text.primary, fontWeight: 600 }}>
              {department.name}
            </Typography>
            <Box sx={{ ml: 2 }}>
              <ThemedChip
                variant={department.isActive ? "success" : "warning"}
                size="medium"
              >
                {department.isActive ? "Active" : "Inactive"}
              </ThemedChip>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Edit Button */}
            <ThemedTooltip 
              title={
                canEdit 
                  ? "Edit department information" 
                  : "Administrator privileges required to modify department records"
              }
              placement="top"
            >
              <span>
                <IconButton
                  onClick={handleEdit}
                  disabled={!canEdit}
                  sx={{ 
                    color: canEdit 
                      ? currentTheme.text.primary 
                      : currentTheme.text.disabled || '#999',
                    cursor: canEdit ? 'pointer' : 'not-allowed'
                  }}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </ThemedTooltip>

            {/* Status Toggle */}
            <StatusToggleButton
              entity={department}
              module="department"
              onToggle={handleStatusToggle}
            />
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Basic Information Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              backgroundColor: currentTheme.callhistory.secondaryBg,
              border: `1px solid ${currentTheme.border.primary}`,
              height: '100%'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessIcon sx={{ mr: 1, color: currentTheme.text.primary }} />
                  <Typography variant="h6" sx={{ color: currentTheme.text.primary, fontWeight: 600 }}>
                    Department Information
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: currentTheme.text.secondary, fontWeight: 500 }}>
                      Department Name
                    </Typography>
                    <Typography variant="body1" sx={{ color: currentTheme.text.primary }}>
                      {department.name}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ color: currentTheme.text.secondary, fontWeight: 500 }}>
                      Fax Number
                    </Typography>
                    <Typography variant="body1" sx={{ color: currentTheme.text.primary }}>
                      {department.faxNumber ? formatPhoneNumberForList(department.faxNumber) : 'Not provided'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ color: currentTheme.text.secondary, fontWeight: 500 }}>
                      Created
                    </Typography>
                    <Typography variant="body1" sx={{ color: currentTheme.text.primary }}>
                      {department.createdAt ? new Date(department.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ color: currentTheme.text.secondary, fontWeight: 500 }}>
                      Last Updated
                    </Typography>
                    <Typography variant="body1" sx={{ color: currentTheme.text.primary }}>
                      {department.updatedAt ? new Date(department.updatedAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Facility Information Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              backgroundColor: currentTheme.callhistory.secondaryBg,
              border: `1px solid ${currentTheme.border.primary}`,
              height: '100%'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocalHospitalIcon sx={{ mr: 1, color: currentTheme.text.primary }} />
                  <Typography variant="h6" sx={{ color: currentTheme.text.primary, fontWeight: 600 }}>
                    Facility Information
                  </Typography>
                </Box>

                {facility ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: currentTheme.text.secondary, fontWeight: 500 }}>
                        Facility Name
                      </Typography>
                      <Typography variant="body1" sx={{ color: currentTheme.text.primary }}>
                        {facility.facilityName}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ color: currentTheme.text.secondary, fontWeight: 500 }}>
                        Address
                      </Typography>
                      <Typography variant="body1" sx={{ color: currentTheme.text.primary }}>
                        {facility.location ? 
                          `${facility.location.streetAddress}, ${facility.location.city}, ${facility.location.state} ${facility.location.zipcode}` :
                          'Address not available'
                        }
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ color: currentTheme.text.secondary, fontWeight: 500 }}>
                        Phone Number
                      </Typography>
                      <Typography variant="body1" sx={{ color: currentTheme.text.primary }}>
                        {facility.phoneNumber ? formatPhoneNumberForList(facility.phoneNumber) : 'Not provided'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ color: currentTheme.text.secondary, fontWeight: 500 }}>
                        Facility Fax
                      </Typography>
                      <Typography variant="body1" sx={{ color: currentTheme.text.primary }}>
                        {facility.faxNumber ? formatPhoneNumberForList(facility.faxNumber) : 'Not provided'}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ color: currentTheme.text.secondary }}>
                    Facility information not available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Staff Members Card */}
          {department.staff && department.staff.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                backgroundColor: currentTheme.callhistory.secondaryBg,
                border: `1px solid ${currentTheme.border.primary}`,
                height: '100%'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ mr: 1, color: currentTheme.text.primary }} />
                    <Typography variant="h6" sx={{ color: currentTheme.text.primary, fontWeight: 600 }}>
                      Assigned Staff ({department.staff.length})
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '300px', overflowY: 'auto' }}>
                    {department.staff.map((staffMember, index) => (
                      <Box 
                        key={staffMember.id || index}
                        sx={{ 
                          p: 2, 
                          border: `1px solid ${currentTheme.border.secondary}`,
                          borderRadius: 1,
                          backgroundColor: currentTheme.callhistory.primaryBg
                        }}
                      >
                        <Typography variant="body1" sx={{ color: currentTheme.text.primary, fontWeight: 500 }}>
                          {staffMember.name || `${staffMember.firstName || ''} ${staffMember.lastName || ''}`.trim()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: currentTheme.text.secondary }}>
                          {staffMember.email}
                        </Typography>
                        {staffMember.role && (
                          <ThemedChip variant="default" size="small" sx={{ mt: 1 }}>
                            {staffMember.role}
                          </ThemedChip>
                        )}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Rendering Providers Card */}
          {department.renderingProviders && department.renderingProviders.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                backgroundColor: currentTheme.callhistory.secondaryBg,
                border: `1px solid ${currentTheme.border.primary}`,
                height: '100%'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocalHospitalIcon sx={{ mr: 1, color: currentTheme.text.primary }} />
                    <Typography variant="h6" sx={{ color: currentTheme.text.primary, fontWeight: 600 }}>
                      Rendering Providers ({department.renderingProviders.length})
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '300px', overflowY: 'auto' }}>
                    {department.renderingProviders.map((provider, index) => (
                      <Box 
                        key={provider.id || index}
                        sx={{ 
                          p: 2, 
                          border: `1px solid ${currentTheme.border.secondary}`,
                          borderRadius: 1,
                          backgroundColor: currentTheme.callhistory.primaryBg
                        }}
                      >
                        <Typography variant="body1" sx={{ color: currentTheme.text.primary, fontWeight: 500 }}>
                          {provider.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: currentTheme.text.secondary }}>
                          NPI: {provider.npiNumber || 'Not provided'}
                        </Typography>
                        {provider.specialties && provider.specialties.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {provider.specialties.map((specialty, idx) => (
                              <ThemedChip key={idx} variant="default" size="small">
                                {specialty}
                              </ThemedChip>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </CustomContainer>
  );
};

export default DepartmentDetails;
